import type { ReactNode } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import { IconChevronDown, IconChevronUp } from '@noths/polaris-client-ribbons-design-system';

import { EXPAND_ACCORDION_EVENT_NAME } from '../../../../customEvents';
import { Content } from '../Content/Content';
import { STICKY_HEADER_SPACER_HEIGHT_PX } from '../StickyHeader/StickyHeader';
import * as styles from './Accordion.styles';

export interface AccordionProps {
  backgroundColor: string;
  title: string;
  darkTitleColor: boolean;
  id: string;
  children: ReactNode;
  open?: boolean;
}

export const Accordion = ({
  backgroundColor,
  title,
  darkTitleColor,
  id,
  children,
  open = false,
}: AccordionProps) => {
  const [isOpen, setIsOpen] = useState(open);
  const [scrollIntoView, setScrollIntoView] = useState(false);
  const summaryRef = useRef<HTMLDivElement>(null);
  const iconColor = darkTitleColor ? styles.DARK_COLOR : styles.LIGHT_COLOR;

  const toggleIsOpen = () => (isOpen ? setIsOpen(false) : setIsOpen(true));

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    const keyCode = e.key;

    if (['Enter', ' ', 'Spacebar'].includes(keyCode)) {
      e.preventDefault();
      toggleIsOpen();
    }
  };

  const handleOnClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    toggleIsOpen();
  };

  useEffect(() => {
    if (scrollIntoView) {
      const element = document.getElementById(id);

      if (element) {
        window.scrollTo({
          top: element.offsetTop - STICKY_HEADER_SPACER_HEIGHT_PX,
          behavior: 'smooth',
        });
      }
    }

    setScrollIntoView(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollIntoView]);

  useEffect(() => {
    const expandAccordionListener = (event: CustomEvent) => {
      if (event.detail.accordionID === id) {
        if (event.detail.focusSummary) {
          summaryRef.current?.focus();
        }
        setIsOpen(true);
        setScrollIntoView(true);
      }
    };

    document.addEventListener(
      EXPAND_ACCORDION_EVENT_NAME,
      expandAccordionListener as EventListener,
    );

    return () => {
      document.removeEventListener(
        EXPAND_ACCORDION_EVENT_NAME,
        expandAccordionListener as EventListener,
      );
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <details id={id} open={isOpen} style={{ backgroundColor }}>
      <summary
        onClick={handleOnClick}
        onKeyDown={handleKeyDown}
        aria-expanded={isOpen}
        ref={summaryRef}
        css={styles.summary}
      >
        <Content>
          <h2 css={[styles.title, darkTitleColor ? styles.titleDark : styles.titleLight]}>
            {title}
            {isOpen ? (
              <IconChevronUp css={styles.icon} color={iconColor} />
            ) : (
              <IconChevronDown css={styles.icon} color={iconColor} />
            )}
          </h2>
        </Content>
      </summary>
      {children}
    </details>
  );
};
